import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import FrequentSelect from "./FrequentSelect";
import {
    CYLINDER_OPTIONS, UMWELTPLAKETTEN, FEATURE_OPTIONS,
    ACCESSORY_OPTIONS, WARRANTY_TYPES, FUEL_OPTIONS, DRIVE_TYPES,
    TRANSMISSION_OPTIONS,
} from "../../lib/listingConstants";
import { Zap, Battery, Sparkles, Wrench, Loader2, Wand2, Check, X, AlertTriangle, Image as ImageIcon } from "lucide-react";
import CollapsibleCard from "./CollapsibleCard";
import SectionToggle from "./SectionToggle";
import ImageGridSection from "./ImageGridSection";
import ServiceDocsUpload from "./ServiceDocsUpload";
import { useBasicsCollapse } from "./BasicsCollapseContext";
import MonthYearPicker from "./MonthYearPicker";
import { deriveLicenseClass, deriveAnnualTaxEUR } from "../../lib/licenseAndTax";

/**
 * Phase 3.57 — Erweiterte Fahrzeug-Felder.
 *
 *   • Zylinder-Anzahl (Motorrad-only)
 *   • Umweltplakette (Motorrad-only)
 *   • Ausstattung (Checkbox-Liste)
 *   • Zubehör (Checkbox-Liste)
 *   • Service-Block: Garantie-Typ + Monate, letzter Service, Werkstatt geprüft, TÜV bis
 *   • Elektro-Felder (Reichweite, Ladedauer, Batteriezertifikat) — nur bei fuel=Elektro
 *
 * Feb-2026 (User-Wunsch 24 May Iter 14): Sofortmitnahme/Abholung, Probefahrten,
 * Lieferung und Service-Doc-Upload sind nach „Werbung & Reichweite"
 * (AdReachSection) verschoben. Service-Block enthält jetzt nur noch
 * Garantie/Wartung/TÜV und Service-Notizen.
 *
 * Pure UI-Komponente — kein eigener State.
 */
// eslint-disable-next-line no-unused-vars
export default function ExtraTechnicalSection({ form, update, setForm, isMoto, descriptionNode, user, orderMode = false }) {
    const isElectric = form.fuel === "Elektro";
    const [featuresAi, setFeaturesAi] = useState({ loading: false });
    // Feb-2026 (User-Idee 24 May Iter 28): KI-Vervollständigung für Service-Notes.
    const [serviceAi, setServiceAi] = useState({ loading: false, suggestions: [] });
    const fetchServiceNoteSuggestions = async () => {
        // Feb-2026 Iter 28 (User-Wunsch 25 May): Beschreibung & Service-Notes
        // wurden zu EINEM Feld zusammengefasst → Draft kommt jetzt aus form.description.
        const draft = (form.description || "").trim();
        if (draft.length < 2) {
            toast.info('Bitte zuerst einen kurzen Stichpunkt in die Beschreibung eintippen (z. B. "Reifen neu").');
            return;
        }
        setServiceAi({ loading: true, suggestions: [] });
        try {
            const { data } = await api.post("/listings/ai-service-notes-complete", {
                draft,
                brand: form.brand,
                model: form.model,
                year: form.first_registration_date
                    ? parseInt(form.first_registration_date.slice(0, 4), 10)
                    : form.year,
                mileage: Number(form.mileage) || 0,
            });
            setServiceAi({ loading: false, suggestions: data?.suggestions || [] });
            if (data?.mocked) {
                toast.warning("KI nicht erreichbar — einfache Heuristik angezeigt.");
            }
        } catch (e) {
            setServiceAi({ loading: false, suggestions: [] });
            toast.error(e?.response?.data?.detail || "KI-Vorschlag fehlgeschlagen.");
        }
    };
    const applyServiceNoteSuggestion = (s) => {
        update("description", s);
        setServiceAi({ loading: false, suggestions: [] });
        toast.success("Vorschlag in Beschreibung übernommen.");
    };

    // Feb-2026 Iter 28 — KI-Schadens-Aggregation (C). Sammelt Schadens-Befunde
    // aus allen Inserats-Fotos und fasst sie via Gemini zu einer prägnanten
    // Übersicht zusammen, die der Händler mit einem Klick in die Service-Notes
    // übernehmen kann.
    const [damagesAi, setDamagesAi] = useState({ loading: false, progress: null, summary: "", items: [], mocked: false });
    // Feb-2026 Iter 28 (User-Wunsch) — Akkordeon für die Extras-Row:
    // immer nur eine der 3 Karten (features / service / images) offen,
    // die offene spannt sich über die ganze Breite. Außerdem wird beim Öffnen
    // ANY der 3 Karten die Fahrzeug-Stammdaten-Section automatisch zugeklappt.
    const [openExtra, setOpenExtra] = useState(null);
    const { closeBasicsIfOpen } = useBasicsCollapse();
    const openOne = (key) => (v) => {
        if (v && closeBasicsIfOpen) closeBasicsIfOpen();
        setOpenExtra(v ? key : null);
    };
    const isOpenFeatures = openExtra === "features";
    const isOpenService = openExtra === "service";
    const isOpenImages = openExtra === "images";
    const fetchDamagesAggregate = async () => {
        const imgs = form.images || [];
        if (!imgs.length) {
            toast.info("Bitte zuerst Fotos hochladen.");
            return;
        }
        const labels = form.image_labels || [];
        const backend = process.env.REACT_APP_BACKEND_URL;
        setDamagesAi({ loading: true, progress: { done: 0, total: imgs.length }, summary: "", items: [], mocked: false });

        // Schritt 1 — parallel alle Bilder analysieren (Concurrency 3, um die LLM-Quota zu schonen)
        const concurrency = 3;
        const results = new Array(imgs.length);
        let done = 0;
        const queue = imgs.map((src, i) => ({ src, i }));
        const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const job = queue.shift();
                if (!job) break;
                const relUrl = backend && job.src.startsWith(backend) ? job.src.slice(backend.length) : job.src;
                try {
                    const { data } = await api.post("/listings/ai-image-analyze", { image_url: relUrl });
                    results[job.i] = {
                        image_label: labels[job.i] || data?.suggested_label || "Foto",
                        items: data?.detected_damages || [],
                    };
                } catch {
                    results[job.i] = { image_label: labels[job.i] || "Foto", items: [] };
                }
                done += 1;
                setDamagesAi((p) => ({ ...p, progress: { done, total: imgs.length } }));
            }
        });
        await Promise.all(workers);

        const damages = results.filter((r) => r && r.items.length > 0);
        if (damages.length === 0) {
            setDamagesAi({ loading: false, progress: null, summary: "", items: [], mocked: false });
            toast.success("KI hat keine Schäden auf den Fotos erkannt — gepflegtes Fahrzeug.");
            return;
        }

        // Schritt 2 — Aggregator-Endpoint aufrufen
        try {
            const { data } = await api.post("/listings/ai-damages-aggregate", {
                damages,
                brand: form.brand,
                model: form.model,
            });
            setDamagesAi({
                loading: false,
                progress: null,
                summary: data?.summary || "",
                items: data?.items || [],
                mocked: !!data?.mocked,
            });
            if (data?.mocked) {
                toast.info(`${data?.count || 0} Befunde gesammelt (KI im Fallback-Modus).`);
            } else {
                toast.success(`${data?.count || 0} Befunde von ${damages.length} Fotos zusammengefasst.`);
            }
        } catch (e) {
            setDamagesAi({ loading: false, progress: null, summary: "", items: [], mocked: false });
            toast.error(e?.response?.data?.detail || "Schadens-Aggregation fehlgeschlagen.");
        }
    };
    const applyDamagesToDescription = () => {
        if (!damagesAi.summary) return;
        const existing = (form.description || "").trim();
        const merged = existing
            ? `${existing}\n\n${damagesAi.summary}`
            : damagesAi.summary;
        update("description", merged.slice(0, 4000));
        setDamagesAi((p) => ({ ...p, summary: "", items: [] }));
        toast.success("Schadens-Übersicht in Beschreibung übernommen.");
    };

    const toggleFeature = (val) => {
        const cur = form.features || [];
        update("features", cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
    };
    const toggleAccessory = (val) => {
        const cur = form.accessories || [];
        update("accessories", cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
    };


    // Jul-2026 (User-Wunsch): KI berücksichtigt Marke + Modell + Modelljahr
    // (FIN > Erstzulassung) und recherchiert optional live im Web. Gemeinsame
    // Nutzlast + Anwendung (Features-Häkchen + Freitext-Vorschläge).
    const buildAiPayload = () => ({
        description: (form.description || "").trim(),
        brand: form.brand || "",
        model: form.model || "",
        vin: form.vin || "",
        year: form.first_registration_date
            ? parseInt(String(form.first_registration_date).slice(0, 4), 10)
            : (form.year || null),
    });

    const applyFeatureResult = (data) => {
        const detected = Array.isArray(data?.features) ? data.features : [];
        const existing = Array.isArray(form.features) ? form.features : [];
        const merged = [...existing];
        detected.forEach((f) => {
            if (!merged.includes(f) && FEATURE_OPTIONS.includes(f)) merged.push(f);
        });
        const addedFeat = merged.length - existing.length;
        if (addedFeat > 0) update("features", merged);

        // Freitext-Vorschläge (außerhalb des Vokabulars) → Freitextfeld ergänzen.
        const free = Array.isArray(data?.freetext_suggestions) ? data.freetext_suggestions : [];
        let addedFree = 0;
        if (free.length) {
            const curNotes = (form.equipment_notes || "").trim();
            const curLower = curNotes.toLowerCase();
            const fresh = free.filter((s) => s && !curLower.includes(String(s).toLowerCase()));
            addedFree = fresh.length;
            if (fresh.length) {
                const joined = fresh.join(", ");
                update("equipment_notes", (curNotes ? `${curNotes}, ${joined}` : joined).slice(0, 4000));
            }
        }
        return { addedFeat, addedFree, detected, web: !!data?.web_used, modelYear: data?.model_year };
    };

    const detectFeaturesFromDescription = async () => {
        const desc = (form.description || "").trim();
        const hasBM = !!(form.brand && form.model);
        if (desc.length < 20 && !hasBM) {
            toast.info("Bitte Beschreibung (≥ 20 Zeichen) ODER Marke + Modell angeben.");
            return;
        }
        setFeaturesAi({ loading: true });
        try {
            const { data } = await api.post("/listings/ai-feature-detect", buildAiPayload());
            const { addedFeat, addedFree, detected, web } = applyFeatureResult(data);
            if (detected.length === 0 && addedFree === 0) {
                toast.info(data.mocked
                    ? "KI nicht erreichbar — nichts erkannt."
                    : "Keine zusätzlichen Merkmale erkannt.");
            } else {
                const parts = [];
                if (addedFeat > 0) parts.push(`${addedFeat} Merkmal(e)`);
                if (addedFree > 0) parts.push(`${addedFree} Freitext-Vorschlag/-vorschläge`);
                toast.success(`${parts.join(" + ") || "Ergebnisse"} übernommen${web ? " (inkl. Web-Recherche)" : ""}.`);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Feature-Erkennung fehlgeschlagen.");
        } finally {
            setFeaturesAi({ loading: false });
        }
    };

    // Feb-2026 Iter 28 (User-Wunsch 25 May): Header-„KI"-Button für die Service-Section.
    // Stößt ALLE relevanten KI-Agenten in diesem Bereich an:
    //   1. Schadens-Aggregation aus Foto-Vision (füllt Service-Notes mit Käufer-
    //      orientierter Schadens-Übersicht)
    //   2. Optional: Service-Note-Vervollständigung, falls bereits ein Draft existiert
    const runAllServiceAi = async () => {
        const hasImages = (form.images || []).length > 0;
        // Feb-2026 Iter 28 (User-Wunsch 25 May): Service-Notes & Beschreibung
        // wurden zu einem Feld zusammengefasst → Draft kommt aus form.description.
        const hasDraft = (form.description || "").trim().length >= 2;
        if (!hasImages && !hasDraft) {
            toast.info("Noch keine Daten: Bitte zuerst Fotos hochladen ODER einen kurzen Service-Stichpunkt eintippen.");
            return;
        }
        // Aufklappen, damit User die Ergebnisse direkt sieht
        if (!isOpenService) {
            openOne("service")(true);
        }
        if (hasImages) await fetchDamagesAggregate();
        if (hasDraft) await fetchServiceNoteSuggestions();
    };
    const serviceAiBusy = damagesAi.loading || serviceAi.loading;

    // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v4): KI-Auto-Detection für
    // Ausstattung & Zubehör — Features aus der Beschreibung extrahieren.
    const [featuresAiLoading, setFeaturesAiLoading] = useState(false);
    const runAllFeaturesAi = async () => {
        const desc = (form.description || "").trim();
        const hasBM = !!(form.brand && form.model);
        if (desc.length < 20 && !hasBM) {
            toast.info("Bitte Marke + Modell wählen ODER eine kurze Beschreibung (≥ 20 Zeichen) eintippen — die KI schlägt dann passende Ausstattung (inkl. Serienausstattung des Modelljahres) vor.");
            if (!isOpenService) openOne("service")(true);
            return;
        }
        if (!isOpenFeatures) openOne("features")(true);
        setFeaturesAiLoading(true);
        try {
            const { data } = await api.post("/listings/ai-feature-detect", buildAiPayload());
            const { addedFeat, addedFree, detected, web } = applyFeatureResult(data);
            if (detected.length === 0 && addedFree === 0) {
                toast.info("KI hat keine zusätzlichen Ausstattungs-Merkmale erkannt.");
            } else {
                const parts = [];
                if (addedFeat > 0) parts.push(`${addedFeat} Merkmal(e) markiert`);
                if (addedFree > 0) parts.push(`${addedFree} Freitext-Vorschlag/-vorschläge ergänzt`);
                toast.success(`KI: ${parts.join(" + ")}${web ? " (inkl. Web-Recherche)" : ""}.`);
            }
            if (data?.mocked) {
                toast.warning("KI nicht erreichbar — einfache Heuristik angewendet.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "KI-Feature-Erkennung fehlgeschlagen.");
        } finally {
            setFeaturesAiLoading(false);
        }
    };

    // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v4): Counters für Hint-Anzeige.
    const featuresCount = (form.features || []).length + (form.accessories || []).length;
    const serviceFilledCount = [
        !!form.has_service_book,
        !!form.workshop_inspected,
        !!form.tuev_neu,
        !!(form.warranty_type && form.warranty_type !== "gesetzlich"),
        !!form.warranty_months,
        !!form.last_service_date,
        !!form.tuev_until,
        ((form.description || "").trim().length >= 2),
    ].filter(Boolean).length;
    const serviceFilledTotal = 8;

    return (
        <>
            {/* Phase 3.69 — Motor-Felder wurden ans Ende der Basics-Sektion in CreateListingPage verschoben. */}

            {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v3): Beschreibung &
                Service-Hinweise wurden in die Service-&-Wartung-Card verschoben
                (siehe unten, direkt unter dem Garantie/TÜV-Grid). Kein eigenes
                Akkordeon mehr für die Beschreibung. */}

            {/* === Elektro-spezifische Felder ===
                Aug-2026 (User-Wunsch): Block entfernt — alle E-Antrieb-Felder sind
                jetzt EINMALIG in den Stammdaten („Zusatzinformationen E-Antrieb",
                BasicsSection) zusammengeführt, inkl. Ladedauer + KI-Recherche. */}

            {/* === Ausstattung & Zubehör + Service & Wartung + Bilder & Video ===
                 Feb-2026 (User-Wunsch Iter 28): Drei Cards in einer Zeile nebeneinander
                 (Desktop), übereinander (Mobile). Bilder & Video wurde aus
                 FooterActionsSection hierher gezogen, damit der Block zwischen
                 Stammdaten und Preis-Sektion einen einheitlichen 3-Spalten-Row
                 bildet. */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start" data-testid="extras-row">
            <div className={`${(isOpenFeatures || orderMode) ? "lg:col-span-3" : ""} ${(openExtra && !isOpenFeatures && !orderMode) ? "lg:hidden" : ""}`} data-testid="extras-cell-features">
            <CollapsibleCard
                title="Ausstattung & Zubehör"
                icon={Sparkles}
                hint={`${featuresCount} ${featuresCount === 1 ? "Merkmal" : "Merkmale"} ausgewählt`}
                testId="features-section"
                open={isOpenFeatures}
                onOpenChange={openOne("features")}
                headerExtras={(
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); runAllFeaturesAi(); }}
                        disabled={featuresAiLoading}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] disabled:opacity-50"
                        data-testid="features-ai-refresh-btn"
                        title="KI extrahiert Ausstattungs-Merkmale aus der Beschreibung und markiert sie hier automatisch."
                        aria-label="Alle KI in Ausstattung & Zubehör aktualisieren"
                    >
                        {featuresAiLoading
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />}
                        <span>KI</span>
                    </button>
                )}
            >
                <h4 className="text-[11px] font-bold uppercase tracking-widest text-gray-600 mb-2" data-testid="accessories-section">
                    Zubehör — mitgeliefert (Top-Case, Helm, Ladekabel …)
                </h4>
                <div className="flex flex-wrap gap-2">
                    {ACCESSORY_OPTIONS.map((a) => {
                        const active = (form.accessories || []).includes(a);
                        return (
                            <label key={a} className={`inline-flex items-center gap-2 px-3 py-2 border rounded-sm cursor-pointer text-sm transition-colors ${active ? "border-[#111827] bg-[#FFCC00]/10" : "border-gray-200 hover:border-gray-400"}`} data-testid={`accessory-row-${a}`}>
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={() => toggleAccessory(a)}
                                    className="w-4 h-4 accent-[#111827]"
                                    data-testid={`accessory-${a}`}
                                />
                                {a}
                            </label>
                        );
                    })}
                </div>

                {isMoto && (
                    <>
                        <div className="mt-5 border-t border-gray-200 pt-4" />
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[11px] font-bold uppercase tracking-widest text-gray-600">Ausstattung — Komfort, Sicherheit & Elektronik</h4>
                            <button
                                type="button"
                                onClick={detectFeaturesFromDescription}
                                disabled={featuresAi.loading || !form.description || form.description.length < 20}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                data-testid="features-ai-detect-btn"
                                title="KI scannt die Beschreibung und schlägt passende Features aus der Liste vor."
                            >
                                {featuresAi.loading
                                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Erkenne…</>
                                    : <><Sparkles className="w-3 h-3" /> Aus Beschreibung erkennen</>}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {FEATURE_OPTIONS.map((f) => {
                                const active = (form.features || []).includes(f);
                                return (
                                    <label key={f} className={`flex items-center gap-2 px-3 py-2 border rounded-sm cursor-pointer text-sm transition-colors ${active ? "border-[#111827] bg-[#FFCC00]/10" : "border-gray-200 hover:border-gray-400"}`} data-testid={`feature-row-${f}`}>
                                        <input
                                            type="checkbox"
                                            checked={active}
                                            onChange={() => toggleFeature(f)}
                                            className="w-4 h-4 accent-[#111827]"
                                            data-testid={`feature-${f}`}
                                        />
                                        {f}
                                    </label>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Jun-2026 (User-Wunsch) — Freitextfeld für Zubehör/Ausstattung,
                    das sich keiner Standard-Checkbox zuordnen lässt (z. B. beim
                    Excel-Import nicht gemappte Begriffe wie „Tempomat",
                    „Volldigitales Kombiinstrument"). */}
                <div className="mt-5 border-t border-gray-200 pt-4" data-testid="equipment-notes-block">
                    <Label className="text-[11px] font-bold uppercase tracking-widest text-gray-600 mb-1.5 block">
                        Sonstiges Zubehör / Ausstattung (Freitext)
                    </Label>
                    <textarea
                        value={form.equipment_notes || ""}
                        onChange={(e) => update("equipment_notes", e.target.value.slice(0, 4000))}
                        rows={3}
                        placeholder="Weitere Ausstattung, die oben nicht als Häkchen verfügbar ist (z. B. Tempomat, Spurhalteassistent, Sonderlackierung …)"
                        className="w-full text-sm border border-gray-300 rounded-sm px-3 py-2 resize-y"
                        data-testid="form-equipment-notes"
                    />
                </div>
            </CollapsibleCard>
            </div>

            {/* Feb-2026 (User-Wunsch 22 May): „Zusatzinformationen"-Section komplett
                entfernt — Inserent muss keine zusätzliche Beschreibung mehr eingeben.
                form.description bleibt im State (leer per Default) und wird so an
                das Backend übergeben. */}

            {/* === Service-Block (collapsible) === */}
            {!orderMode && (
            <div className={`${isOpenService ? "lg:col-span-3" : ""} ${(openExtra && !isOpenService) ? "lg:hidden" : ""}`} data-testid="extras-cell-service">
            <CollapsibleCard
                title="Service & Wartung"
                icon={Wrench}
                hint={`${serviceFilledCount} / ${serviceFilledTotal} Felder ausgefüllt`}
                testId="service-section"
                open={isOpenService}
                onOpenChange={openOne("service")}
                headerExtras={(
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); runAllServiceAi(); }}
                        disabled={serviceAiBusy}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] disabled:opacity-50"
                        data-testid="service-ai-refresh-btn"
                        title="Stößt alle KI-Agenten in diesem Bereich an: Schadens-Übersicht aus Fotos + Service-Note-Vervollständigung."
                        aria-label="Alle KI in Service & Wartung aktualisieren"
                    >
                        {serviceAiBusy
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />}
                        <span>KI</span>
                    </button>
                )}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Phase 3.72 — Quick-Toggles ganz oben (Scheckheft / Werkstatt-geprüft / TÜV neu) */}
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                        <input type="checkbox" checked={!!form.has_service_book} onChange={(e) => update("has_service_book", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="form-service-book" />
                        Scheckheft gepflegt
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                        <input type="checkbox" checked={!!form.workshop_inspected} onChange={(e) => update("workshop_inspected", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="form-workshop-inspected" />
                        Werkstatt-geprüft
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                        <input type="checkbox" checked={!!form.tuev_neu} onChange={(e) => update("tuev_neu", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="form-tuev-neu" />
                        TÜV neu
                    </label>
                </div>
                {/* Feb-2026 (User-Wunsch 24 May Iteration 5) — Garantie / Dauer /
                    letzte Wartung / TÜV bis in einer Zeile (4 Spalten auf Desktop). */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">Garantie</Label>
                        <FrequentSelect
                            storageKey="listing.warranty_type"
                            value={form.warranty_type || "any"}
                            onValueChange={(v) => update("warranty_type", v === "any" ? "" : v)}
                            options={[{ value: "any", label: "– keine Angabe (gesetzliche Gewährleistung) –" }, ...WARRANTY_TYPES]}
                            placeholder="– keine Angabe –"
                            triggerClassName="mt-1.5 rounded-sm"
                            testId="form-warranty-type"
                            noFreqValues={["any"]}
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">
                            Garantie-Dauer (Monate)
                            {form.warranty_type === "gesetzlich" && <span className="text-gray-400 font-normal normal-case ml-2">— optional</span>}
                        </Label>
                        <Input type="number" min="0" max="240" value={form.warranty_months} onChange={(e) => update("warranty_months", e.target.value)} placeholder={form.warranty_type === "gesetzlich" ? "z. B. 12" : "z. B. 24"} className="mt-1.5 rounded-sm font-mono-num" data-testid="form-warranty-months" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">Letzte Wartung (Monat / Jahr)</Label>
                        <div className="mt-1.5">
                            <MonthYearPicker
                                value={form.last_service_date || ""}
                                onChange={(v) => update("last_service_date", v)}
                                yearsBack={30}
                                yearsAhead={0}
                                testId="form-last-service-date"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase text-gray-500">TÜV bis (Monat / Jahr)</Label>
                        <div className="mt-1.5">
                            <MonthYearPicker
                                value={form.tuev_until || ""}
                                onChange={(v) => update("tuev_until", v)}
                                yearsBack={2}
                                yearsAhead={8}
                                testId="form-tuev-until"
                            />
                        </div>
                    </div>
                </div>

                {/* §479 BGB Live-Hinweis: bei gewerblicher Garantie (Hersteller/Drittanbieter)
                    den Händler an die Pflichtangaben erinnern (abmahnsicher inserieren). */}
                {(form.warranty_type === "hersteller" || form.warranty_type === "drittanbieter") && (
                    <div className="md:col-span-2 mt-2 flex items-start gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-300 rounded-sm p-2.5" data-testid="warranty-479-editor-hint">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                        <span>
                            <b>§ 479 BGB:</b> Bei beworbener Garantie müssen Käufer:innen die vollständigen Garantiebedingungen
                            (Inhalt, Dauer, räumlicher Geltungsbereich, Name &amp; Anschrift des Garantiegebers, Geltendmachung)
                            in Textform <b>vor Vertragsschluss</b> erhalten. Käufer:innen wird automatisch der Hinweis angezeigt,
                            dass die <b>gesetzliche Gewährleistung unberührt</b> bleibt. Bitte halte die Garantiebedingungen bereit.
                        </span>
                    </div>
                )}

                {/* Phase 3.73 — Sofortmitnahme / Abholung + Probefahrten + Service-Docs:
                    Feb-2026 (User-Wunsch 24 May Iter 14) — diese Blöcke wurden komplett
                    in „Werbung & Reichweite" (AdReachSection) verschoben, weil sie
                    Reichweite-/Conversion-Mechaniken sind. Hier nicht mehr rendern. */}

                {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v3): Beschreibung &
                    Service-Hinweise direkt unter dem Garantie/Wartung/TÜV-Grid in
                    der Service-Card, keine eigene Akkordeon-Karte mehr. */}
                {descriptionNode && (
                    <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-100" data-testid="description-block-wrap">
                        {/* Feb-2026 (User-Wunsch) — Dokumenten-Upload (Serviceheft /
                            Rechnungen) LINKS neben „Beschreibung & Service-Hinweise".
                            Aus der Disposition hierher verschoben. Inkl. KI-Auswertung
                            der Dokumente → Beschreibungsvorschlag. */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                            <div data-testid="service-docs-col">
                                <ServiceDocsUpload form={form} update={update} />
                            </div>
                            <div data-testid="description-col">
                                {descriptionNode}
                            </div>
                        </div>
                    </div>
                )}

                {/* Feb-2026 Iter 28 (User-Wunsch 25 May): Service-Notes-Textarea
                    entfernt — Beschreibung & Service-Hinweise sind jetzt EIN Feld
                    (form.description). KI-Buttons & Ergebnis-Karten bleiben hier,
                    schreiben aber direkt in die Beschreibung oben. */}
                <div className="md:col-span-2 mt-2 border-t border-gray-100 pt-4">
                    {/* KI-Schadens-Aggregations-Ergebnis */}
                    {damagesAi.summary && (
                        <div className="mt-3 bg-amber-50 border-2 border-amber-300 rounded-sm p-3" data-testid="damages-aggregate-result">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                                    <Wand2 className="w-3.5 h-3.5" /> KI-Schadens-Übersicht
                                    {damagesAi.mocked && (
                                        <span className="ml-1 text-[9px] font-mono text-amber-700 normal-case">(Fallback)</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setDamagesAi((p) => ({ ...p, summary: "", items: [] }))}
                                    className="text-[11px] text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"
                                    data-testid="damages-aggregate-dismiss"
                                >
                                    <X className="w-3 h-3" /> Schließen
                                </button>
                            </div>
                            <p className="text-sm text-gray-900 bg-white border border-amber-200 rounded-sm p-2.5 leading-snug" data-testid="damages-aggregate-summary">
                                {damagesAi.summary}
                            </p>
                            {damagesAi.items.length > 0 && (
                                <details className="mt-2">
                                    <summary className="text-[11px] text-amber-800 cursor-pointer">
                                        {damagesAi.items.length} Einzel-Befunde anzeigen
                                    </summary>
                                    <ul className="mt-1.5 space-y-0.5">
                                        {damagesAi.items.map((it, i) => (
                                            <li key={i} className="text-[11px] text-gray-700">• {it}</li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                            <button
                                type="button"
                                onClick={applyDamagesToDescription}
                                className="mt-2 inline-flex items-center gap-1.5 bg-[#111827] hover:bg-black text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm"
                                data-testid="damages-aggregate-apply"
                            >
                                <Check className="w-3 h-3" /> In Beschreibung übernehmen
                            </button>
                        </div>
                    )}

                    {/* KI-Vorschlags-Liste */}
                    {serviceAi.suggestions.length > 0 && (
                        <div className="mt-3 bg-amber-50 border-2 border-amber-300 rounded-sm p-3 space-y-2" data-testid="service-notes-suggestions">
                            <div className="flex items-center justify-between">
                                <div className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                                    <Wand2 className="w-3.5 h-3.5" /> KI-Vorschläge ({serviceAi.suggestions.length})
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setServiceAi({ loading: false, suggestions: [] })}
                                    className="text-[11px] text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"
                                    data-testid="service-notes-suggestions-dismiss"
                                >
                                    <X className="w-3 h-3" /> Schließen
                                </button>
                            </div>
                            <ul className="space-y-2">
                                {serviceAi.suggestions.map((s, i) => (
                                    <li key={i} className="flex items-start gap-2 bg-white border border-amber-200 rounded-sm p-2">
                                        <p className="text-sm text-gray-800 flex-1">{s}</p>
                                        <button
                                            type="button"
                                            onClick={() => applyServiceNoteSuggestion(s)}
                                            className="shrink-0 inline-flex items-center gap-1 bg-[#111827] hover:bg-black text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm"
                                            data-testid={`service-notes-apply-${i}`}
                                        >
                                            <Check className="w-3 h-3" /> Übernehmen
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Feb-2026 (User-Wunsch 24 May Iter 26): Führerschein-Klasse
                        + Kfz-Steuer-Karte verschoben aus BasicsSection — passt
                        fachlich besser zu „Service & Wartung", weil beides
                        Betriebs-/Halter-Kosten sind. */}
                    {(form.displacement || form.power_kw) && (() => {
                        const lic = deriveLicenseClass(form);
                        const tax = deriveAnnualTaxEUR(form);
                        if (!lic && !tax) return null;
                        return (
                            <div className="mt-4 bg-emerald-50/60 border border-emerald-200 rounded-sm p-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="license-tax-card">
                                {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v11):
                                    Sichtbares KI-Label „berechnet aus Werks-Daten",
                                    damit Inserent erkennt, dass diese Werte automatisch
                                    aus Hubraum/Leistung abgeleitet sind. */}
                                <div className="md:col-span-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-300 rounded-sm px-2 py-0.5 self-start" data-testid="license-tax-ai-badge">
                                    <Sparkles className="w-3 h-3" />
                                    KI-berechnet aus Hubraum &amp; Leistung
                                </div>
                                {lic && (
                                    <div data-testid="license-class-info">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-0.5">Führerschein-Klasse</div>
                                        <div className="font-bold text-emerald-900 text-base">
                                            {lic.class || "—"}
                                        </div>
                                        <div className="text-xs text-emerald-800/80 mt-0.5">{lic.label}</div>
                                    </div>
                                )}
                                {tax && (
                                    <div data-testid="annual-tax-info">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-0.5">Kfz-Steuer / Jahr (geschätzt)</div>
                                        <div className="font-bold text-emerald-900 text-base font-mono-num">
                                            {tax.amount_eur === 0 ? "0,00 €" : `${tax.amount_eur.toFixed(2).replace('.', ',')} €`}
                                            {tax.monthly_eur > 0 && (
                                                <span className="ml-2 text-xs font-normal text-emerald-700">
                                                    ≈ {tax.monthly_eur.toFixed(2).replace('.', ',')} €/Monat
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-emerald-800/80 mt-0.5">{tax.label}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
                </div>
            </CollapsibleCard>
            </div>
            )}

            {/* Bilder & Video — Feb-2026 Iter 28: hierher verschoben aus FooterActionsSection,
                damit es als 3. Spalte der „Extras-Row" sitzt. setForm wird via Props übergeben.
                Jul-2026: im Bestellmodus ausgeblendet. */}
            {!orderMode && (
            <div className={`${isOpenImages ? "lg:col-span-3" : ""} ${(openExtra && !isOpenImages) ? "lg:hidden" : ""}`} data-testid="extras-cell-images">
            <SectionToggle
                title="Bilder & Video"
                icon={ImageIcon}
                hint={`${(form.images || []).length} Bilder · ${form.video_url ? "Video vorhanden" : "kein Video"}`}
                testId="images-section-toggle"
                open={isOpenImages}
                onOpenChange={openOne("images")}
                headerExtras={(
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!(form.images || []).length) {
                                toast.info("Bitte zuerst Bilder hochladen — KI analysiert dann Labels, Schäden und Qualität.");
                                if (!isOpenImages) openOne("images")(true);
                                return;
                            }
                            if (!isOpenImages) openOne("images")(true);
                            window.dispatchEvent(new CustomEvent("tw:images-ai-refresh-all"));
                        }}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00]"
                        data-testid="images-ai-refresh-btn"
                        title="Stößt alle KI-Foto-Funktionen erneut an: Labels, Schäden-Erkennung, Qualitäts-Score und Auto-Position."
                        aria-label="Alle KI in Bilder & Video aktualisieren"
                    >
                        <Sparkles className="w-3 h-3" />
                        <span>KI</span>
                    </button>
                )}
            >
                <ImageGridSection form={form} setForm={setForm} />
            </SectionToggle>
            </div>
            )}
            </div>
        </>
    );
}
